import { Alert, Box, LinearProgress, Paper, Typography } from "@mui/material";
import type { FC } from "react";
import { useState } from "react";
import CustomerSelect from "../../components/customerSelect/CustomerSelect";
import dayjs, { ISO_DATE_FORMAT } from "../../dayjs";
import KpiSections from "./components/KpiSections";
import OrdersBarChart from "./components/OrdersBarChart";
import RegionBreakdownChart from "./components/RegionBreakdownChart";
import ReportFilterPanel from "./components/ReportFilterPanel";
import EurExchangeRateChip from "./components/EurExchangeRateChip";
import ReportHeader from "./components/ReportHeader";
import { summarySections } from "./kpiSections";
import { regionOrderShareItems } from "./regionChartItems";
import { REPORTS } from "./reportCatalog";
import type { OrdersTrendGranularity } from "./statisticsApi";
import { useAppliedFilters } from "./useAppliedFilters";
import { useCustomerOrdersTrend } from "./useCustomerOrdersTrend";
import { useCustomerStatistics } from "./useCustomerStatistics";

interface DetailFilters {
  readonly customerId: string | null;
  readonly from: string;
  readonly to: string;
}

function defaultDetailFilters(): DetailFilters {
  return {
    customerId: null,
    from: dayjs().startOf("year").format(ISO_DATE_FORMAT),
    to: dayjs().format(ISO_DATE_FORMAT),
  };
}

const ClientDetailReportPage: FC = () => {
  const { draft, applied, isDirty, setDraft, apply } = useAppliedFilters<DetailFilters>(defaultDetailFilters);
  const [granularity, setGranularity] = useState<OrdersTrendGranularity>("MONTH");
  const range = { from: applied.from, to: applied.to };
  const statistics = useCustomerStatistics(applied.customerId, { ...range, includeMargin: true });
  const trend = useCustomerOrdersTrend(applied.customerId, { ...range, granularity });
  const customerName = statistics.data?.customerName;

  return (
    <Box>
      <ReportHeader report={REPORTS["customer-details"]} />
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: "1px solid rgba(0,0,0,0.08)", borderRadius: 2 }}>
        <Typography variant="subtitle2" component="h2" sx={{ fontWeight: "bold", mb: 2 }}>
          Filtry raportu
        </Typography>
        <ReportFilterPanel
          subject={
            <CustomerSelect
              size="small"
              customerId={draft.customerId}
              onChange={(customerId) => setDraft({ ...draft, customerId })}
            />
          }
          dateLabel="Zakres dat"
          range={draft}
          onRangeChange={({ from, to }) => setDraft({ ...draft, from, to })}
          onApply={apply}
          applyDisabled={!isDirty}
        />
        <Typography variant="body2" sx={{ color: "text.secondary", mt: -2 }}>
          Raport dla: <strong>{customerName ?? "—"}</strong> · {applied.from} — {applied.to}
        </Typography>
      </Paper>
      {!applied.customerId && (
        <Typography sx={{ color: "text.secondary" }}>Wybierz klienta, aby zobaczyć raport</Typography>
      )}
      {(statistics.isLoading || trend.isLoading) && <LinearProgress data-testid="loading-indicator" />}
      {statistics.error && <Alert severity="error">Nie udało się pobrać statystyk.</Alert>}
      {statistics.data && (
        <>
          <EurExchangeRateChip eurExchangeRate={statistics.data.eurExchangeRate} />
          <KpiSections sections={summarySections(statistics.data.summary)} layout="grid" />
        </>
      )}
      {trend.error && <Alert severity="error">Nie udało się pobrać wykresu zamówień.</Alert>}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 3, alignItems: "start" }}>
        {statistics.data && (
          <RegionBreakdownChart
            title="Ilość zamówień — podział na regiony"
            items={regionOrderShareItems(statistics.data.byRegion)}
          />
        )}
        {trend.data && (
          <OrdersBarChart
            title={`Zamówienia aktywne — ${customerName ?? "klient"}`}
            buckets={trend.data.buckets}
            granularity={granularity}
            onGranularityChange={setGranularity}
          />
        )}
      </Box>
    </Box>
  );
};

export default ClientDetailReportPage;
